// UserAuditLogs.js

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { Container, TextField, Paper, Box } from '@mui/material';
import Typography from '../../../../atomicDesign/atoms/Text';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './users.scss';
import { useParams } from 'react-router-dom';
import CustomDateInput from '../../../../components/CustomDateInput/CustomDateInput';
import { getFullName } from '../../../../utils/user';
import { useTablePreferences } from '../../../../customhooks/useTablePreferences';
import AuditRequestDetails, { AuditChangeSummary } from './AuditChanges';
import AdminContext from '../../../../context/admin';

const commonTextFieldStyles = {
  '& .MuiOutlinedInput-root': { height: '56px' },
  '& .MuiInputBase-input': { border: 'none', boxShadow: 'none' },
};

// Calculate default dates: from one month ago to today
const initialDateUntil = new Date();
const initialDateFrom = new Date();
initialDateFrom.setMonth(initialDateFrom.getMonth() - 1);

// Helper function to generate a friendly summary for the main table view.
// The content is wrapped with pointerEvents: 'none' so that clicks pass to the row.
const generateChangeSummary = (body, method) => {
  if (!body || typeof body !== 'object' || Object.keys(body).length === 0) {
    return (
      <span style={{ pointerEvents: 'none' }}>
        <strong style={{ color: '#333333' }}>
          No change details available
        </strong>
      </span>
    );
  }
  if (method === 'POST') {
    return (
      <span style={{ pointerEvents: 'none' }}>
        <strong style={{ color: 'green' }}>Record created</strong>
      </span>
    );
  }
  if (method === 'DELETE') {
    return (
      <span style={{ pointerEvents: 'none' }}>
        <strong style={{ color: 'red' }}>Record deleted</strong>
      </span>
    );
  }
  if (method === 'PUT') {
    const keys = Object.keys(body).filter(
      (key) => !['_id', '__v', 'createdAt', 'updatedAt'].includes(key)
    );
    return keys.length > 0 ? (
      <span style={{ pointerEvents: 'none' }}>
        <strong style={{ color: 'blue' }}>Record updated:</strong>{' '}
        {keys.join(', ')}
      </span>
    ) : (
      <span style={{ pointerEvents: 'none' }}>
        <strong style={{ color: 'blue' }}>Record updated</strong>
      </span>
    );
  }
  return (
    <span style={{ pointerEvents: 'none' }}>
      <strong>Record updated</strong>
    </span>
  );
};

// Expanded row: what the request changed in the database (AuditRequestDetails),
// then the data that was sent, rendered recursively.
const ExpandedChangeDetails = ({ data }) => (
  <AuditRequestDetails changes={data.changes} read={data.method === 'GET'}>
    <SentData body={data.body} />
  </AuditRequestDetails>
);

const SentData = ({ body }) => {
  // If body is empty or not an object, display fallback message with existing styles
  if (!body || typeof body !== 'object' || Object.keys(body).length === 0) {
    return (
      <div
        style={{
          padding: '10px 20px',
          fontStyle: 'italic',
          color: '#999',
          fontSize: '14px',
          textAlign: 'center',
        }}
      >
        No change details available.
      </div>
    );
  }

  // Recursive function to render an object, preserving styling and adding indentation for nested objects
  const renderObject = (obj, indent = 0) => {
    return Object.entries(obj).map(([key, value]) => {
      const containerStyle = {
        marginBottom: '4px',
        paddingLeft: indent + 'px',
      };
      if (value && typeof value === 'object') {
        return (
          <div key={key} style={containerStyle}>
            <strong>{key}:</strong>
            <div>{renderObject(value, indent + 10)}</div>
          </div>
        );
      }
      return (
        <div key={key} style={containerStyle}>
          <strong>{key}:</strong> {String(value)}
        </div>
      );
    });
  };

  return (
    <div
      style={{
        padding: '10px 20px',
        whiteSpace: 'pre-wrap',
        fontFamily: 'monospace',
        cursor: 'pointer',
      }}
    >
      {renderObject(body)}
    </div>
  );
};

// Custom styles for DataTable header cells

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
      fontSize: '14px',
      backgroundColor: '#f0f0f0',
    },
  },
  pagination: {
    style: {
      borderTop: '1px solid #eee',
      minHeight: '56px',
      paddingLeft: '24px',
      paddingRight: '24px',
      backgroundColor: '#fff',
    },
  },
  paginationPageButtons: {
    style: {
      borderRadius: '50%',
      backgroundColor: 'transparent',
      color: '#1976d2',
      margin: '0 4px',
      transition: 'background-color 0.3s, box-shadow 0.3s',
      '&:hover': {
        backgroundColor: '#e3f2fd',
      },

      '& button#pagination-previous-page.sc-koXPp.DfDUN[disabled], & [id="pagination-previous-page"].sc-koXPp.DfDUN[disabled]':
        {
          backgroundColor: '#fff !important',
          color: 'rgba(0, 0, 0, 0.18) !important',
          fill: 'rgba(0, 0, 0, 0.18) !important',
          cursor: 'not-allowed !important',
          boxShadow: 'none !important',
        },
      // Забезпечуємо, що при hover на цю disabled кнопку стиль не змінюється
      '& button#pagination-previous-page.sc-koXPp.DfDUN[disabled]:hover, & [id="pagination-previous-page"].sc-koXPp.DfDUN[disabled]:hover':
        {
          backgroundColor: '#fff !important',
          boxShadow: 'none !important',
        },
    },
  },
};

const UserAuditLogs = () => {
  // Extract userId from URL parameters
  const { userId } = useParams();

  // State variables for logs, loading, error, filter text, and date range
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // the API answers 403 below level 4
  const [noAccess, setNoAccess] = useState(false);
  const { language } = useContext(AdminContext);
  const [filterText, setFilterText] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateUntil, setDateUntil] = useState(initialDateUntil);
  const { preferences, savePreferences } =
    useTablePreferences('user-audit-logs');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = {};
        if (dateFrom) {
          params.from = dateFrom.toISOString().split('T')[0];
        }
        if (dateUntil) {
          params.until = dateUntil.toISOString().split('T')[0];
        }
        if (userId) {
          params.sessionId = userId;
        }
        const response = await axios.get('/api/v1/log-route', {
          withCredentials: true,
          params,
        });
        // console.log('API response:', response.data);
        if (response.data.success) {
          setLogs(response.data.data);
        } else {
          setError('Failed to load audit logs.');
        }
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 403) {
          setNoAccess(true);
        } else {
          setError('Error loading audit logs.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [userId, dateFrom, dateUntil]);

  // Define columns for the DataTable
  const columns = [
    {
      id: 'createdAt',
      name: 'Date/Time',
      selector: (row) => new Date(row.createdAt).toLocaleString(),
      sortable: true,
      width: '180px',
    },
    // {
    //     name: 'Method',
    //     selector: row => row.method,
    //     sortable: true,
    //     width: '100px',
    // },
    {
      id: 'name',
      name: 'Name',
      selector: (row) => (row.body && row.body.name ? row.body.name : '-'),
      sortable: true,
      width: '150px',
    },
    {
      id: 'doneBy',
      name: language?.settings?.userManagement?.auditLogs?.doneBy ?? 'Done by',
      selector: (row) => getFullName(row.session) ?? row.session?.email ?? '-',
      sortable: true,
      width: '170px',
    },
    {
      id: 'vendor',
      name: 'Vendor',
      // Use vendorName from session as the vendor
      selector: (row) =>
        row.session && row.session.vendorName ? row.session.vendorName : '-',
      sortable: true,
      width: '150px',
    },
    {
      id: 'url',
      name: 'URL',
      cell: (row) => {
        let cleanedUrl = row.url || '';
        // Remove '/api' prefix if present
        cleanedUrl = cleanedUrl.replace(/^\/api/, '');
        // Remove query parameters if present
        if (cleanedUrl.indexOf('?') !== -1) {
          cleanedUrl = cleanedUrl.split('?')[0];
        }
        // Remove segments that are 24 hex characters (e.g., /67d9752c003d3da49ade20d4)
        cleanedUrl = cleanedUrl.replace(/\/[0-9a-f]{23,24}/g, '');
        return <span style={{ pointerEvents: 'none' }}>{cleanedUrl}</span>;
      },
      sortable: true,
      width: '250px',
    },
    {
      name: 'Change Details',
      cell: (row) =>
        row.method === 'GET' ? (
          <span style={{ pointerEvents: 'none' }}>
            <strong>
              {language?.settings?.userManagement?.auditLogs?.viewed ??
                'Viewed'}
            </strong>
          </span>
        ) : row.changes?.length > 0 ? (
          <AuditChangeSummary changes={row.changes} />
        ) : (
          generateChangeSummary(row.body, row.method)
        ),
      sortable: false,
      wrap: true,
    },
  ];

  // Filter logs based on URL, Method, or raw Change Details text
  const filteredLogs = logs.filter((log) => {
    const details =
      (log.body ? JSON.stringify(log.body) : '') +
      (log.changes?.length ? JSON.stringify(log.changes) : '');
    return (
      (log.url && log.url.toLowerCase().includes(filterText.toLowerCase())) ||
      (log.method &&
        log.method.toLowerCase().includes(filterText.toLowerCase())) ||
      (details && details.toLowerCase().includes(filterText.toLowerCase()))
    );
  });

  // Get user details from the first log entry if available
  // The list also has requests other admins made on this user, so take the
  // name from a request the user made themselves.
  const userDetails =
    logs.find((log) => log.session && String(log.session.id) === userId)
      ?.session ?? null;

  return (
    <Container>
      {/* Display user details (full name and email) at the top */}
      {userDetails && (
        <>
          <Typography variant="h5" gutterBottom>
            {getFullName(userDetails)}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            {userDetails.email}
          </Typography>
        </>
      )}
      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <TextField
          label="Filter Records (URL, Name, or Change Details)"
          variant="outlined"
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{ marginBottom: '16px' }}
          sx={{
            ...commonTextFieldStyles,
          }}
        />
        <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              width: '100%',
            }}
          >
            <DatePicker
              selected={dateFrom}
              onChange={(date) => setDateFrom(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select From Date"
              customInput={<CustomDateInput label="From Date" />}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              width: '100%',
            }}
          >
            <DatePicker
              selected={dateUntil}
              onChange={(date) => setDateUntil(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select To Date"
              customInput={<CustomDateInput label="To Date" />}
            />
          </Box>
        </Box>
      </Paper>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : noAccess ? (
        <Typography color="text.secondary">
          {language?.settings?.userManagement?.auditLogs?.noAccess ??
            'You need level 4 (System Admin) or higher to see audit logs.'}
        </Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <DataTable
          columns={columns}
          data={filteredLogs}
          pagination
          paginationPerPage={
            [10, 20, 50, 100].includes(preferences.rowsPerPage)
              ? preferences.rowsPerPage
              : 10
          }
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            savePreferences({ rowsPerPage: currentRowsPerPage });
          }}
          highlightOnHover
          pointerOnHover
          defaultSortFieldId={preferences.sortFieldId ?? 'createdAt'}
          defaultSortAsc={preferences.sortAsc ?? true}
          onSort={(column, sortDirection) => {
            if (column.id !== undefined) {
              savePreferences({
                sortFieldId: column.id,
                sortAsc: sortDirection === 'asc',
              });
            }
          }}
          responsive
          noHeader
          expandOnRowClicked
          expandableRows
          expandableRowDisabled={() => false}
          expandableRowsComponent={ExpandedChangeDetails}
          customStyles={customStyles}
        />
      )}
    </Container>
  );
};

export default UserAuditLogs;
